import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import CountrySelector from '@/components/CountrySelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Auto-detect country by IP on mount (pre-fill optional field; user can change)
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const resp = await axios.get(`${API}/detect-language`, { timeout: 2500, signal: controller.signal });
        const code = (resp.data?.country || '').toUpperCase();
        if (code && code.length === 2) setCountry((prev) => prev || code);
      } catch (_) { /* silent: optional field */ }
    })();
    return () => controller.abort();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !fullName) {
      toast.error(t('errors.fillAllFields'));
      return;
    }

    if (fullName.trim().length < 3) {
      toast.error(t('errors.nameMinLength'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('errors.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      await register(email, password, fullName, country || null);
      setRegistered(true);
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || t('errors.registerError'));
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div style={{ 
              width: '64px', 
              height: '64px', 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <span style={{ fontSize: '2rem' }}>✉️</span>
            </div>
            <h1>{t('auth.checkEmail')}</h1>
            <p style={{ marginTop: '1rem', lineHeight: '1.6' }}>
              {t('auth.confirmationSent')}<br/>
              <strong>{email}</strong>
            </p>
          </div>

          <div style={{ 
            background: '#f0f9ff', 
            padding: '1rem', 
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #bfdbfe'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#1e40af' }}>
              📬 {t('auth.clickLinkToConfirm')}
            </p>
          </div>

          <div style={{ 
            background: '#fef3c7', 
            padding: '1rem', 
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #fbbf24'
          }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e' }}>
              ⚠️ {t('auth.checkSpam')}
            </p>
          </div>

          <div className="auth-footer">
            <p>
              <Link to="/login" data-testid="login-link">
                {t('auth.backToLogin')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <UserPlus size={32} />
          <h1>{t('auth.register')}</h1>
          <p>{t('auth.createAccount')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')} *</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={loading}
              data-testid="register-email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fullName">{t('auth.fullName')} *</label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('auth.fullNamePlaceholder')}
              disabled={loading}
              data-testid="register-fullname"
              minLength={3}
            />
            <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              {t('auth.minChars', { count: 3 })}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')} *</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              data-testid="register-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">{t('auth.country')}</label>
            <CountrySelector
              value={country}
              onChange={setCountry}
              disabled={loading}
              placeholder={t('auth.countryPlaceholder')}
            />
            <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              {t('auth.countryHint')}
            </small>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="auth-submit-btn"
            data-testid="register-submit"
          >
            {loading ? t('auth.registering') : t('auth.registerButton')}
          </Button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.haveAccount')}{' '}
            <Link to="/login" data-testid="login-link">
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
